"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, FileText, Award, CircleCheck, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const certifications = [{
  id: "fda-registration",
  title: "FDA Registered",
  subtitle: "Federal Compliance",
  description: "Our facility is fully registered with the FDA, ensuring all custom carbonated wellness drinks are produced under rigorous federal safety standards and technical oversight.",
  badgeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/fda_registered_badge.png",
  certImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/fda_certificate.png"
}, {
  id: "gmp-certified",
  title: "GMP Certified",
  subtitle: "Manufacturing Excellence",
  description: "Strict adherence to Good Manufacturing Practices (GMP) guarantees the highest level of purity, consistency, and quality in every batch of your functional beverages.",
  badgeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/gmp_certified_badge.png",
  certImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/gmp_certificate.png"
}, {
  id: "organic-certified",
  title: "Certified Organic",
  subtitle: "Natural Integrity",
  description: "We provide certified organic processing for brands prioritizing clean-label ingredients, ensuring your probiotic fizzes maintain their natural integrity from tank to can.",
  badgeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/organic_certified_badge.png",
  certImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/organic_certificate.png"
}, {
  id: "compliance-support",
  title: "Safety Support",
  subtitle: "Regulatory Expertise",
  description: "Beyond production, we provide comprehensive technical documentation and regulatory guidance to ensure your product is fully compliant for national retail distribution.",
  badgeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/compliance_support_badge.png",
  certImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/compliance_certificate.png"
}];
export default function CertificationsAndWarranty() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="141899" id="compliance-safety" className="bg-background py-20 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05] bg-[radial-gradient(circle_at_center,_var(--color-primary)_1px,_transparent_1px)] [background-size:24px_24px]"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-full mb-4">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-6">
            Compliance & Safety
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Our commitment to technical excellence is verified by industry-leading certifications. We ensure every custom RTD beverage meets the highest safety and regulatory standards for the wellness market.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {certifications.map((cert, i) => {
          const cardReveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.1
          });
          return <div key={cert.id} data-index={i} ref={cardReveal.ref} className={`transition-all duration-700 delay-[${i * 100}ms] ${cardReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <Dialog>
                  <DialogTrigger asChild>
                    <div className="h-full cursor-pointer group outline-none">
                      <Card className="h-full bg-card border-border/50 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-primary/40 hover:-translate-y-1 overflow-hidden relative">
                        {}
                        <div className="absolute top-0 left-0 w-full h-1 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
                        
                        <CardContent className="p-8 flex flex-col items-center text-center h-full">
                          <div className="w-28 h-28 rounded-2xl bg-muted/50 flex items-center justify-center mb-6 p-4 transition-transform duration-500 group-hover:scale-110 group-hover:bg-primary/10 relative">
                            <img src={cert.badgeImg} alt={`Badge for ${cert.title}`} className="w-full h-full object-contain drop-shadow-sm" />
                            <div className="absolute -bottom-2 -right-2 bg-background rounded-full p-1.5 shadow-sm border border-border opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <ZoomIn className="w-4 h-4 text-primary" />
                            </div>
                          </div>
                          
                          <h3 className="text-xl font-semibold text-foreground mb-1">{cert.title}</h3>
                          <p className="text-sm font-medium text-primary mb-3 uppercase tracking-wider">{cert.subtitle}</p>
                          
                          <p className="text-sm text-muted-foreground line-clamp-3 mb-6 flex-grow">
                            {cert.description}
                          </p>
                          
                          
                        </CardContent>
                      </Card>
                    </div>
                  </DialogTrigger>
                  
                  <DialogContent className="sm:max-w-2xl md:max-w-3xl lg:max-w-4xl p-0 overflow-hidden bg-card border-border shadow-2xl">
                    <div className="grid md:grid-cols-[1fr_300px] h-full max-h-[85vh]">
                      {}
                      <div className="bg-muted/10 p-8 flex items-center justify-center relative min-h-[300px] md:min-h-[500px]">
                        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/grid_pattern.png)] opacity-5 mix-blend-multiply"></div>
                        <img src={cert.certImg} alt={`Official Certification for ${cert.title}`} className="max-w-full max-h-full object-contain drop-shadow-md relative z-10" />
                      </div>
                      
                      {}
                      <div className="p-6 md:p-8 flex flex-col bg-card border-t md:border-t-0 md:border-l border-border">
                        <DialogHeader className="text-left mb-6 space-y-2">
                          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-lg w-fit mb-2">
                            <Award className="w-5 h-5 text-primary" />
                          </div>
                          <DialogTitle className="text-2xl font-bold text-foreground">{cert.title}</DialogTitle>
                          <DialogDescription className="text-base font-medium text-primary uppercase tracking-tight">
                            {cert.subtitle}
                          </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 flex-grow">
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {cert.description}
                          </p>
                          
                          <div className="pt-4 space-y-3">
                            <div className="flex items-start gap-3">
                              <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                              <span className="text-sm text-foreground">FDA facility registration active and verified.</span>
                            </div>
                            <div className="flex items-start gap-3">
                              <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                              <span className="text-sm text-foreground">GMP audit protocols strictly enforced.</span>
                            </div>
                            <div className="flex items-start gap-3">
                              <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                              <span className="text-sm text-foreground">Regulatory documentation package included.</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-8 pt-6 border-t border-border text-xs text-muted-foreground text-center">
                          Official compliance documentation issued for Custom RTD Co. Manufacturing Facility.
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>;
        })}
        </div>
      </div>
    </section>;
}